/**
 * SoleadLab 2.0 — Spatial Entity API Client
 *
 * V4 Phase 2.6/2.7: Frontend service for overlay + orthophoto entity CRUD.
 *
 * Read (P2.6): fetchEntityOverlays, fetchEntityOrthophotos — authority-based read switch
 * Write (P2.7): create/update/delete for overlays + orthophotos with optimistic concurrency
 *
 * Transforms between backend row shape (snake_case, JSON strings, 0/1
 * integers) and frontend Overlay/Orthophoto shapes (camelCase, parsed
 * objects, booleans).
 */

import type { Overlay, Orthophoto } from '@/stores/projectStore';
import { apiFetch, apiFetchWithEtag, isConflictError } from './apiFetch';

/** Alias kept for back-compat; spatial entities historically used a distinct name. */
export const isSpatialConflictError = isConflictError;

// ─── Backend Row Types ──────────────────────────────────────────────────────

interface OverlayRow {
  id: string;
  site_id: string;
  name: string;
  r2_url: string;
  bounds_json: string;
  opacity: number;
  visible: number;
  capture_date: string | null;
  tileset_id: string | null;
  tile_status: string | null;
  source_r2_key: string | null;
  /** Plan-bake parameters as JSON (0052) — null for non-PDF overlays */
  bake_json: string | null;
  /** Companion-DXF ingest state as JSON (0053) — null when no DXF attached */
  dxf_json: string | null;
  sort_order: number;
  version: number;
  created_at: string;
  updated_at: string;
}

interface OrthophotoRow {
  id: string;
  site_id: string;
  name: string;
  r2_url: string;
  coordinates_json: string;
  bounds_json: string;
  opacity: number;
  visible: number;
  is_manual: number;
  file_name: string | null;
  file_size: number | null;
  width: number | null;
  height: number | null;
  capture_date: string | null;
  tileset_id: string | null;
  tile_status: string | null;
  source_r2_key: string | null;
  bg_removed: number;
  sort_order: number;
  version: number;
  created_at: string;
  updated_at: string;
}

interface ListResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  migrated: boolean;
}

// ─── Result Types ──────────────────────────────────────────────────────────

export interface EntityOverlayResult {
  migrated: boolean;
  overlays: Overlay[];
}

export interface EntityOrthophotoResult {
  migrated: boolean;
  orthophotos: Orthophoto[];
}

// ─── Fetch Utilities (thin adapters around centralized apiFetch) ──────────────

async function apiFetchGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

async function apiFetchMutate<T>(
  path: string,
  method: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<{ data: T; etag: number }> {
  return apiFetchWithEtag<T>(path, {
    method,
    headers: extraHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function apiFetchDelete(
  path: string,
  extraHeaders?: Record<string, string>,
): Promise<void> {
  await apiFetch<void>(path, { method: 'DELETE', headers: extraHeaders });
}

// ─── Transforms ────────────────────────────────────────────────────────────

/** Backend overlay row → frontend Overlay shape */
function overlayRowToOverlay(row: OverlayRow): Overlay {
  let bounds: Overlay['bounds'];
  try {
    bounds = JSON.parse(row.bounds_json);
  } catch {
    bounds = [[0, 0], [0, 0], [0, 0], [0, 0]];
  }

  // Unparseable bake params degrade to "never baked" (the Bake button comes
  // back) rather than failing the whole overlay load.
  let bake: Overlay['bake'];
  if (row.bake_json) {
    try {
      bake = JSON.parse(row.bake_json);
    } catch {
      bake = undefined;
    }
  }

  // Same degradation for DXF state: unparseable → "no DXF attached".
  let dxf: Overlay['dxf'];
  if (row.dxf_json) {
    try {
      dxf = JSON.parse(row.dxf_json);
    } catch {
      dxf = undefined;
    }
  }

  return {
    id: row.id,
    name: row.name,
    url: row.r2_url,
    bounds,
    opacity: row.opacity,
    visible: row.visible === 1,
    captureDate: row.capture_date ?? undefined,
    tilesetId: row.tileset_id ?? undefined,
    tileStatus: (row.tile_status as Overlay['tileStatus']) ?? undefined,
    sourceR2Key: row.source_r2_key ?? undefined,
    bake,
    dxf,
    _version: row.version,
  };
}

/** Backend orthophoto row → frontend Orthophoto shape */
function orthophotoRowToOrthophoto(row: OrthophotoRow): Orthophoto {
  let coordinates: Orthophoto['coordinates'];
  try {
    coordinates = JSON.parse(row.coordinates_json);
  } catch {
    coordinates = [[0, 0], [0, 0], [0, 0], [0, 0]];
  }

  let bounds: Orthophoto['bounds'];
  try {
    bounds = JSON.parse(row.bounds_json);
  } catch {
    bounds = { minLng: 0, minLat: 0, maxLng: 0, maxLat: 0 };
  }

  return {
    id: row.id,
    name: row.name,
    url: row.r2_url,
    coordinates,
    bounds,
    opacity: row.opacity,
    visible: row.visible === 1,
    isManual: row.is_manual === 1 ? true : undefined,
    fileName: row.file_name ?? undefined,
    fileSize: row.file_size ?? undefined,
    width: row.width ?? undefined,
    height: row.height ?? undefined,
    captureDate: row.capture_date ?? undefined,
    tilesetId: row.tileset_id ?? undefined,
    tileStatus: (row.tile_status as Orthophoto['tileStatus']) ?? undefined,
    sourceR2Key: row.source_r2_key ?? undefined,
    bgRemoved: row.bg_removed === 1 ? true : undefined,
    sortOrder: row.sort_order,
    _version: row.version,
  };
}

// ─── Read API ──────────────────────────────────────────────────────────────

/**
 * Fetch ALL overlays for a site from the entity API.
 * Auto-paginates. Returns overlays + migration authority signal.
 */
export async function fetchEntityOverlays(
  siteId: string,
): Promise<EntityOverlayResult> {
  const allOverlays: Overlay[] = [];
  let page = 1;
  let migrated = false;
  const limit = 200;

  while (true) {
    const result = await apiFetchGet<ListResponse<OverlayRow>>(
      `/api/v2/sites/${siteId}/overlays?page=${page}&limit=${limit}`,
    );

    if (page === 1) {
      migrated = result.migrated;
    }

    for (const row of result.items) {
      allOverlays.push(overlayRowToOverlay(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { migrated, overlays: allOverlays };
}

/**
 * Fetch ALL orthophotos for a site from the entity API.
 * Auto-paginates. Returns orthophotos + migration authority signal.
 */
export async function fetchEntityOrthophotos(
  siteId: string,
): Promise<EntityOrthophotoResult> {
  const allOrthophotos: Orthophoto[] = [];
  let page = 1;
  let migrated = false;
  const limit = 200;

  while (true) {
    const result = await apiFetchGet<ListResponse<OrthophotoRow>>(
      `/api/v2/sites/${siteId}/orthophotos?page=${page}&limit=${limit}`,
    );

    if (page === 1) {
      migrated = result.migrated;
    }

    for (const row of result.items) {
      allOrthophotos.push(orthophotoRowToOrthophoto(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { migrated, orthophotos: allOrthophotos };
}

// ─── Reverse Transforms (Frontend → Backend) ─────────────────────────────

/** Frontend Overlay → entity API body for POST */
function overlayToCreateBody(ov: Overlay): Record<string, unknown> {
  return {
    id: ov.id,
    name: ov.name,
    r2_url: ov.url,
    bounds_json: JSON.stringify(ov.bounds),
    opacity: ov.opacity,
    visible: ov.visible ? 1 : 0,
    capture_date: ov.captureDate ?? null,
    tileset_id: ov.tilesetId ?? null,
    tile_status: ov.tileStatus ?? null,
    source_r2_key: ov.sourceR2Key ?? null,
    bake_json: ov.bake ? JSON.stringify(ov.bake) : null,
    dxf_json: ov.dxf ? JSON.stringify(ov.dxf) : null,
  };
}

/** Frontend Overlay partial → entity API body for PATCH (only defined fields) */
function overlayUpdatesToPatchBody(updates: Partial<Overlay>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (updates.name !== undefined) body.name = updates.name;
  if (updates.url !== undefined) body.r2_url = updates.url;
  if (updates.bounds !== undefined) body.bounds_json = JSON.stringify(updates.bounds);
  if (updates.opacity !== undefined) body.opacity = updates.opacity;
  if (updates.visible !== undefined) body.visible = updates.visible ? 1 : 0;
  if (updates.captureDate !== undefined) body.capture_date = updates.captureDate ?? null;
  if (updates.tilesetId !== undefined) body.tileset_id = updates.tilesetId ?? null;
  if (updates.tileStatus !== undefined) body.tile_status = updates.tileStatus ?? null;
  if (updates.sourceR2Key !== undefined) body.source_r2_key = updates.sourceR2Key ?? null;
  if (updates.bake !== undefined) body.bake_json = updates.bake ? JSON.stringify(updates.bake) : null;
  if (updates.dxf !== undefined) body.dxf_json = updates.dxf ? JSON.stringify(updates.dxf) : null;
  return body;
}

/** Frontend Orthophoto → entity API body for POST */
function orthophotoToCreateBody(op: Orthophoto): Record<string, unknown> {
  return {
    id: op.id,
    name: op.name,
    r2_url: op.url,
    coordinates_json: JSON.stringify(op.coordinates),
    bounds_json: JSON.stringify(op.bounds),
    opacity: op.opacity,
    visible: op.visible ? 1 : 0,
    is_manual: op.isManual ? 1 : 0,
    file_name: op.fileName ?? null,
    file_size: op.fileSize ?? null,
    width: op.width ?? null,
    height: op.height ?? null,
    capture_date: op.captureDate ?? null,
    tileset_id: op.tilesetId ?? null,
    tile_status: op.tileStatus ?? null,
    source_r2_key: op.sourceR2Key ?? null,
    bg_removed: op.bgRemoved ? 1 : 0,
    sort_order: op.sortOrder ?? 0,
  };
}

/** Frontend Orthophoto partial → entity API body for PATCH (only defined fields) */
function orthophotoUpdatesToPatchBody(updates: Partial<Orthophoto>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (updates.name !== undefined) body.name = updates.name;
  if (updates.url !== undefined) body.r2_url = updates.url;
  if (updates.coordinates !== undefined) body.coordinates_json = JSON.stringify(updates.coordinates);
  if (updates.bounds !== undefined) body.bounds_json = JSON.stringify(updates.bounds);
  if (updates.opacity !== undefined) body.opacity = updates.opacity;
  if (updates.visible !== undefined) body.visible = updates.visible ? 1 : 0;
  if (updates.isManual !== undefined) body.is_manual = updates.isManual ? 1 : 0;
  if (updates.fileName !== undefined) body.file_name = updates.fileName ?? null;
  if (updates.fileSize !== undefined) body.file_size = updates.fileSize ?? null;
  if (updates.width !== undefined) body.width = updates.width ?? null;
  if (updates.height !== undefined) body.height = updates.height ?? null;
  if (updates.captureDate !== undefined) body.capture_date = updates.captureDate ?? null;
  if (updates.tilesetId !== undefined) body.tileset_id = updates.tilesetId ?? null;
  if (updates.tileStatus !== undefined) body.tile_status = updates.tileStatus ?? null;
  if (updates.sourceR2Key !== undefined) body.source_r2_key = updates.sourceR2Key ?? null;
  if (updates.bgRemoved !== undefined) body.bg_removed = updates.bgRemoved ? 1 : 0;
  if (updates.sortOrder !== undefined) body.sort_order = updates.sortOrder;
  return body;
}

// ─── Write API: Overlays ──────────────────────────────────────────────────

/**
 * Create an overlay in the entity table.
 * Returns the created overlay with _version from server.
 */
export async function createEntityOverlay(
  siteId: string,
  overlay: Overlay,
): Promise<Overlay> {
  const body = overlayToCreateBody(overlay);
  const { data: row, etag } = await apiFetchMutate<OverlayRow>(
    `/api/v2/sites/${siteId}/overlays`,
    'POST',
    body,
  );
  const result = overlayRowToOverlay(row);
  result._version = etag || result._version;
  return result;
}

/**
 * Update an overlay in the entity table.
 * Requires _version for optimistic concurrency (If-Match header).
 */
export async function updateEntityOverlay(
  siteId: string,
  id: string,
  updates: Partial<Overlay>,
  version: number,
): Promise<Overlay> {
  const patchBody = overlayUpdatesToPatchBody(updates);
  if (Object.keys(patchBody).length === 0) {
    return { ...(updates as Overlay), id, _version: version };
  }
  const { data: row, etag } = await apiFetchMutate<OverlayRow>(
    `/api/v2/sites/${siteId}/overlays/${id}`,
    'PATCH',
    patchBody,
    { 'If-Match': String(version) },
  );
  const result = overlayRowToOverlay(row);
  result._version = etag || result._version;
  return result;
}

/**
 * Delete an overlay from the entity table.
 * Requires _version for optimistic concurrency (If-Match header).
 */
export async function deleteEntityOverlay(
  siteId: string,
  id: string,
  version: number,
): Promise<void> {
  await apiFetchDelete(
    `/api/v2/sites/${siteId}/overlays/${id}`,
    { 'If-Match': String(version) },
  );
}

/**
 * Fetch a single overlay by ID. Used for conflict reload on 409.
 * Returns null if not found.
 */
export async function fetchSingleEntityOverlay(
  siteId: string,
  id: string,
): Promise<Overlay | null> {
  try {
    const row = await apiFetchGet<OverlayRow>(
      `/api/v2/sites/${siteId}/overlays/${id}`,
    );
    return overlayRowToOverlay(row);
  } catch {
    return null;
  }
}

// ─── Write API: Orthophotos ───────────────────────────────────────────────

/**
 * Create an orthophoto in the entity table.
 * Returns the created orthophoto with _version from server.
 */
export async function createEntityOrthophoto(
  siteId: string,
  orthophoto: Orthophoto,
): Promise<Orthophoto> {
  const body = orthophotoToCreateBody(orthophoto);
  const { data: row, etag } = await apiFetchMutate<OrthophotoRow>(
    `/api/v2/sites/${siteId}/orthophotos`,
    'POST',
    body,
  );
  const result = orthophotoRowToOrthophoto(row);
  result._version = etag || result._version;
  return result;
}

/**
 * Update an orthophoto in the entity table.
 * Requires _version for optimistic concurrency (If-Match header).
 *
 * If `updates` contains no fields the entity API recognises (e.g. caller is
 * clearing a UI-only field like `tileStatus: undefined`), short-circuits and
 * returns the optimistic state without round-tripping — backend rejects empty
 * PATCH bodies as `INVALID_BODY`.
 */
export async function updateEntityOrthophoto(
  siteId: string,
  id: string,
  updates: Partial<Orthophoto>,
  version: number,
): Promise<Orthophoto> {
  const patchBody = orthophotoUpdatesToPatchBody(updates);
  if (Object.keys(patchBody).length === 0) {
    return { ...(updates as Orthophoto), id, _version: version };
  }
  const { data: row, etag } = await apiFetchMutate<OrthophotoRow>(
    `/api/v2/sites/${siteId}/orthophotos/${id}`,
    'PATCH',
    patchBody,
    { 'If-Match': String(version) },
  );
  const result = orthophotoRowToOrthophoto(row);
  result._version = etag || result._version;
  return result;
}

/**
 * Delete an orthophoto from the entity table.
 * Requires _version for optimistic concurrency (If-Match header).
 */
export async function deleteEntityOrthophoto(
  siteId: string,
  id: string,
  version: number,
): Promise<void> {
  await apiFetchDelete(
    `/api/v2/sites/${siteId}/orthophotos/${id}`,
    { 'If-Match': String(version) },
  );
}

/**
 * Fetch a single orthophoto by ID. Used for conflict reload on 409.
 * Returns null if not found.
 */
export async function fetchSingleEntityOrthophoto(
  siteId: string,
  id: string,
): Promise<Orthophoto | null> {
  try {
    const row = await apiFetchGet<OrthophotoRow>(
      `/api/v2/sites/${siteId}/orthophotos/${id}`,
    );
    return orthophotoRowToOrthophoto(row);
  } catch {
    return null;
  }
}
